import React from 'react';
import _ from 'lodash';

import {
  Box,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  styled
} from '@mui/material';
import {
  Schema,
  Validator
} from 'jsonschema';
import {
  errorUtil,
  validationUtil
} from 'iron-forge-react-modules';

import { Editor } from '@monaco-editor/react';
import { NetSuiteWriteRequestFormat } from 'generated/graphql';
import { Error as ErrorIcon } from '@mui/icons-material';
import * as dataService from './dataService';



const RootBox = styled(Box)({
  display: 'flex',
  flexDirection: 'row'
});

const ActionsBox = styled(Box)({
  flex: 1
});


export interface IProps {
  writeRequestId: string;
  type: NetSuiteWriteRequestFormat;
  writeRequestContent: string;
  validationErrors: string[];

  onShouldRefetch: () => void;
}

export const FC: React.FC<IProps> = props => {
  const [
    saveAndValidateWriteRequest,
    saveAndValidateWriteRequestTuple
  ] = dataService.useSaveAndValidateWriteRequest(props.writeRequestId);
  const [
    regenerateWriteRequest,
    regenerateWriteRequestTuple
  ] = dataService.useRegenerateWriteRequest(props.writeRequestId);
  const [code, setCode] = React.useState(props.writeRequestContent);



  const handleClickSave = React.useCallback(
    async () => {
      try {
        const parsedCode = JSON.parse(code);
        const validator = new Validator();
        const result = validator.validate(parsedCode, props.type === 'customer' ? customerSchema : orderSchema);
        if (!_.isEmpty(result.errors)) {
          throw new Error(result.errors[0].toString());
        }

        await errorUtil.errorWrap(async () => {
          await saveAndValidateWriteRequest(parsedCode);
        }, {
          RECORD_WRITTEN_TO_NET_SUITE: "This record is in NetSuite",
          INVALID_ARGS: "Object is not a valid write request."
        });
        props.onShouldRefetch();

      } catch (err) {
        if (err instanceof Error) {
          alert(err.message);
        }
      }
    },
    [
      code,
      props,
      saveAndValidateWriteRequest
    ]
  );

  const handleClickRegenerate = React.useCallback(
    async () => {
      if (window.confirm("This will delete and regenerate the write request.")) {
        await errorUtil.errorWrap(async () => {
          await regenerateWriteRequest();
        }, {
          RECORD_WRITTEN_TO_NET_SUITE: "This record is in NetSuite",
          INVALID_ARGS: "Object is not a valid write request."
        });
        props.onShouldRefetch();
      }
    },
    [
      props,
      regenerateWriteRequest
    ]
  );



  const loading = saveAndValidateWriteRequestTuple.loading || regenerateWriteRequestTuple.loading;

  return (
    <RootBox>
      <ActionsBox>
        <Editor
          value={code}
          onChange={val => setCode(val ?? '')}
          height={500} />
      </ActionsBox>

      <ActionsBox>
        <Box>
          <Typography>
            If a specific edit needed to be made, those changes can be made on the left and saved here.
          </Typography>

          <Button
            disabled={loading || props.writeRequestContent === code}
            onClick={handleClickSave}>
            Save Edits
          </Button>
        </Box>

        <Box>
          <Typography>
            If the store settings have changed, consider regenerating the request instead of editing it.
          </Typography>

          <Button
            color="warning"
            disabled={loading}
            onClick={handleClickRegenerate}>
            Regenerate Request
          </Button>
        </Box>

        <Box>
          <Typography>
            The following below are the errors returned from NetSuite
          </Typography>

          <List>
            {_.map(props.validationErrors, (err, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  <ErrorIcon color="error" />
                </ListItemIcon>

                <ListItemText primary={err} />
              </ListItem>
            ))}
          </List>
        </Box>
      </ActionsBox>
    </RootBox>
  );
};

const __simpleOrEmptyStringSchema: Schema = {
  type: 'string',
  minLength: 0
};

const __simpleStringSchema: Schema = {
  type: 'string',
  minLength: 1
};

const __integerStringSchema: Schema = {
  type: 'string',
  pattern: validationUtil.reDecimalNumber
};

const __numericStringSchema: Schema = {
  type: 'string',
  pattern: /^((-?[0-9]+\.[0-9]*)|(-?[0-9]*\.[0-9]+)|(-?[0-9]+))$/
};

const __usStateAbbreviation: Schema = {
  type: 'string',
  enum: [
    'AL', 'AK', 'AS', 'AZ', 'AR', 'CA',
    'CO', 'CT', 'DE', 'DC', 'FL', 'GA',
    'GU', 'HI', 'ID', 'IL', 'IN', 'IA',
    'KS', 'KY', 'LA', 'ME', 'MD', 'MA',
    'MI', 'MN', 'MS', 'MO', 'MT', 'NE',
    'NV', 'NH', 'NJ', 'NM', 'NY', 'NC',
    'ND', 'MP', 'OH', 'OK', 'OR', 'PA',
    'PR', 'RI', 'SC', 'SD', 'TN', 'TX',
    'UT', 'VT', 'VA', 'VI', 'WA', 'WV',
    'WI', 'WY'
  ]
};

const __addressSchema: Schema = {
  type: 'object',
  properties: {
    'city': { type: 'string', minLength: 0 },
    'phone': { type: 'string', minLength: 0 },
    'company': { type: 'string', minLength: 0 },
    'street1': { type: 'string', minLength: 0 },
    'street2': { type: 'string', minLength: 0 },
    'usState': __usStateAbbreviation,
    'postalCode': { type: 'string', minLength: 0 },
    'countryCode': { type: 'string', minLength: 2, maxLength: 2 }
  },
  required: [
    'city',
    'phone',
    'company',
    'street1',
    'street2',
    'usState',
    'postalCode',
    'countryCode'
  ]
};

const customerSchema: Schema = {
  type: 'object',
  properties: {
    'email': {
      type: 'string',
      minLength: 1,
      maxLength: 1000,
      pattern: /\S+@\S+/
    },
    'phone': { type: 'string', minLength: 1 },
    'billing': __addressSchema,
    'lastName': { type: 'string', minLength: 1 },
    'shipping': __addressSchema,
    'firstName': { type: 'string', minLength: 1 },
    'channelInternalId': __integerStringSchema,
    'taxItemInternalId': __integerStringSchema,
    'subsidiaryInternalId': __integerStringSchema
  },
  required: [
    'email',
    'phone',
    'billing',
    'lastName',
    'shipping',
    'firstName',
    'channelInternalId',
    'taxItemInternalId',
    'subsidiaryInternalId'
  ]
};

const orderSchema: Schema = {
  type: 'object',
  properties: {
    'memo': __simpleStringSchema,
    'isTaxable': {
      type: 'boolean'
    },
    'lineItems': {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          'quantity': {
            type: 'number',
            minimum: 1
          },
          'pricePerUnitCents': __numericStringSchema,
          'inventoryItemInternalId': __integerStringSchema
        },
        required: [
          'quantity',
          'pricePerUnitCents',
          'inventoryItemInternalId'
        ]
      }
    },
    'orderDate': __simpleStringSchema,
    'orderNumber': __simpleStringSchema,
    'termsInternalId': __integerStringSchema,
    'taxItemInternalId': __integerStringSchema,
    'customerInternalId': __integerStringSchema,
    'locationInternalId': __integerStringSchema,
    'shipItemInternalId': __integerStringSchema,
    'shippingTotalCents': __numericStringSchema,
    'departmentInternalId': __integerStringSchema,
    'salesChannelInternalId': __integerStringSchema,
    'paymentMethodInternalId': __integerStringSchema,
    'tpbAccountNumber': __simpleOrEmptyStringSchema,
    'tpbAddressee': __simpleOrEmptyStringSchema,
    'tpbAttention': __simpleOrEmptyStringSchema,
    'tpbCarrierType': __simpleOrEmptyStringSchema,
    'tpbDisplayName': __simpleOrEmptyStringSchema,
    'tpbPhoneNumber': __simpleOrEmptyStringSchema,
    'tpbStreet1': __simpleOrEmptyStringSchema,
    'tpbStreet2': __simpleOrEmptyStringSchema,
    'tpbStreet3': __simpleOrEmptyStringSchema,
    'tpbCity': __simpleOrEmptyStringSchema,
    'tpbUsState': __simpleOrEmptyStringSchema,
    'tpbPostalCode': __simpleOrEmptyStringSchema,
    'tpbCountryCode': __simpleOrEmptyStringSchema
  },
  required: [
    'memo',
    'isTaxable',
    'orderDate',
    'orderNumber',
    'termsInternalId',
    'taxItemInternalId',
    'customerInternalId',
    'locationInternalId',
    'shipItemInternalId',
    'shippingTotalCents',
    'departmentInternalId',
    'salesChannelInternalId',
    'paymentMethodInternalId',
    'tpbAccountNumber',
    'tpbAddressee',
    'tpbAttention',
    'tpbCarrierType',
    'tpbDisplayName',
    'tpbPhoneNumber',
    'tpbStreet1',
    'tpbStreet2',
    'tpbStreet3',
    'tpbCity',
    'tpbUsState',
    'tpbPostalCode',
    'tpbCountryCode'
  ]
};
